import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { formatCurrency } from '../lib/utils';
import {
  Calculator,
  TrendingUp,
  PiggyBank,
  Calendar,
  DollarSign,
  Info,
  Download,
  RefreshCw,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  Legend
} from 'recharts';

// Rendimientos históricos por perfil (datos del Excel original)
const RENDIMIENTOS = {
  conservador: {
    '2019': 0.0812, '2020': 0.0469, '2021': 0.0447, '2022': 0.0446, '2023': 0.0403, '2024': 0.0329
  },
  balanceado: {
    '2019': 0.0747, '2020': 0.0075, '2021': 0.0914, '2022': 0.017, '2023': 0.0219, '2024': 0.011
  },
  dinamico: {
    '2019': 0.1701, '2020': -0.0493, '2021': 0.1688, '2022': -0.0088, '2023': 0.0086, '2024': 0.0022
  }
};

// Promedios de rendimiento
const PROMEDIOS = {
  conservador: 0.055,
  balanceado: 0.045,
  dinamico: 0.06
};

export const SimulatorPage = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    aportacionMensual: 3500,
    plazoAnios: 25,
    perfilInversion: 'conservador',
    rendimientoSupuesto: 12,
    inflacionAnual: 4
  });

  const [showDetails, setShowDetails] = useState(false);
  const [activeTab, setActiveTab] = useState('simulador');

  // Calcular proyección
  const proyeccion = useMemo(() => {
    const { aportacionMensual, plazoAnios, rendimientoSupuesto } = formData;
    const tasaMensual = (rendimientoSupuesto / 100) / 12;
    const meses = plazoAnios * 12;
    
    let saldoAcumulado = 0;
    let totalAportado = 0;
    const datosAnuales = [];
    const datosMensuales = [];

    for (let mes = 1; mes <= meses; mes++) {
      totalAportado += aportacionMensual;
      saldoAcumulado = (saldoAcumulado + aportacionMensual) * (1 + tasaMensual);
      
      datosMensuales.push({
        mes,
        aportado: totalAportado,
        saldo: saldoAcumulado,
        rendimiento: saldoAcumulado - totalAportado
      });

      if (mes % 12 === 0) {
        datosAnuales.push({
          anio: mes / 12,
          aportado: totalAportado,
          saldo: Math.round(saldoAcumulado),
          rendimiento: Math.round(saldoAcumulado - totalAportado)
        });
      }
    }

    return {
      saldoFinal: saldoAcumulado,
      totalAportado,
      rendimientoTotal: saldoAcumulado - totalAportado,
      rendimientoPorcentaje: ((saldoAcumulado - totalAportado) / totalAportado) * 100,
      datosAnuales,
      datosMensuales
    };
  }, [formData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      aportacionMensual: 3500,
      plazoAnios: 25,
      perfilInversion: 'conservador',
      rendimientoSupuesto: 12,
      inflacionAnual: 4
    });
  };

  const exportToCSV = () => {
    const headers = ['Año', 'Total Aportado', 'Saldo Acumulado', 'Rendimiento'];
    const rows = proyeccion.datosAnuales.map(d => [
      d.anio,
      d.aportado,
      d.saldo,
      d.rendimiento
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `simulacion_ahorro_${formData.nombre || 'cliente'}.csv`;
    link.click();
  };

  return (
    <div className="space-y-6" data-testid="simulator-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Calculator className="h-8 w-8 text-[#1a365d]" />
            Simulador de Ahorro
          </h1>
          <p className="text-muted-foreground">OptiMaxx Plus - Proyección de ahorro a largo plazo</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={resetForm}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Reiniciar
          </Button>
          <Button variant="outline" onClick={exportToCSV}>
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="simulador">Simulador</TabsTrigger>
          <TabsTrigger value="rendimientos">Rendimientos Históricos</TabsTrigger>
          <TabsTrigger value="comparador">Comparador</TabsTrigger>
        </TabsList>

        {/* Tab Simulador */}
        <TabsContent value="simulador" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Formulario de entrada */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PiggyBank className="h-5 w-5" />
                  Datos del Cliente
                </CardTitle>
                <CardDescription>Ingresa los parámetros de la simulación</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre del Prospecto</Label>
                  <Input
                    id="nombre"
                    value={formData.nombre}
                    onChange={(e) => handleInputChange('nombre', e.target.value)}
                    placeholder="Nombre del cliente"
                    data-testid="simulator-nombre"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="aportacion">Aportación Mensual (MXN)</Label>
                  <Input
                    id="aportacion"
                    type="number"
                    value={formData.aportacionMensual}
                    onChange={(e) => handleInputChange('aportacionMensual', parseFloat(e.target.value) || 0)}
                    min="500"
                    step="100"
                    data-testid="simulator-aportacion"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="plazo">Plazo Comprometido (años)</Label>
                  <Select
                    value={formData.plazoAnios.toString()}
                    onValueChange={(v) => handleInputChange('plazoAnios', parseInt(v))}
                  >
                    <SelectTrigger data-testid="simulator-plazo">
                      <SelectValue placeholder="Seleccionar plazo" />
                    </SelectTrigger>
                    <SelectContent>
                      {[5, 10, 15, 20, 25].map(anios => (
                        <SelectItem key={anios} value={anios.toString()}>
                          {anios} años
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="perfil">Perfil de Inversión</Label>
                  <Select
                    value={formData.perfilInversion}
                    onValueChange={(v) => handleInputChange('perfilInversion', v)}
                  >
                    <SelectTrigger data-testid="simulator-perfil">
                      <SelectValue placeholder="Seleccionar perfil" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="conservador">Conservador (Bajo riesgo)</SelectItem>
                      <SelectItem value="balanceado">Balanceado (Riesgo medio)</SelectItem>
                      <SelectItem value="dinamico">Dinámico (Alto riesgo)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rendimiento">Supuesto de Rendimiento Anual (%)</Label>
                  <Input
                    id="rendimiento"
                    type="number"
                    value={formData.rendimientoSupuesto}
                    onChange={(e) => handleInputChange('rendimientoSupuesto', parseFloat(e.target.value) || 0)}
                    min="1"
                    max="30"
                    step="0.5"
                    data-testid="simulator-rendimiento"
                  />
                </div>

                <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-sm">
                  <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 mt-0.5 text-blue-600" />
                    <div className="text-muted-foreground">
                      <p className="font-medium text-foreground">Rendimiento promedio histórico:</p>
                      <ul className="mt-1 space-y-1">
                        <li>Conservador: ~5.5% anual</li>
                        <li>Balanceado: ~4.5% anual</li>
                        <li>Dinámico: ~6% anual</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Resultados */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Proyección de Ahorro
                </CardTitle>
                <CardDescription>
                  {formData.nombre ? `Simulación para ${formData.nombre}` : 'Resultados de la simulación'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Resumen */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 rounded-lg bg-muted">
                    <p className="text-sm text-muted-foreground">Total Aportado</p>
                    <p className="text-xl font-bold text-[#1a365d]">
                      {formatCurrency(proyeccion.totalAportado)}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20">
                    <p className="text-sm text-muted-foreground">Saldo Final</p>
                    <p className="text-xl font-bold text-green-600">
                      {formatCurrency(proyeccion.saldoFinal)}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                    <p className="text-sm text-muted-foreground">Rendimiento Total</p>
                    <p className="text-xl font-bold text-blue-600">
                      {formatCurrency(proyeccion.rendimientoTotal)}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-purple-50 dark:bg-purple-900/20">
                    <p className="text-sm text-muted-foreground">Ganancia %</p>
                    <p className="text-xl font-bold text-purple-600">
                      {proyeccion.rendimientoPorcentaje.toFixed(1)}%
                    </p>
                  </div>
                </div>

                {/* Gráfica */}
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={proyeccion.datosAnuales}>
                      <defs>
                        <linearGradient id="colorSaldo" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#1a365d" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#1a365d" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorAportado" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#2d8659" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#2d8659" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="anio" label={{ value: 'Años', position: 'bottom' }} />
                      <YAxis tickFormatter={(value) => `$${(value/1000000).toFixed(1)}M`} />
                      <Tooltip 
                        formatter={(value) => formatCurrency(value)}
                        labelFormatter={(label) => `Año ${label}`}
                      />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="aportado"
                        stroke="#2d8659"
                        fillOpacity={1}
                        fill="url(#colorAportado)"
                        name="Total Aportado"
                      />
                      <Area
                        type="monotone"
                        dataKey="saldo"
                        stroke="#1a365d"
                        fillOpacity={1}
                        fill="url(#colorSaldo)"
                        name="Saldo Acumulado"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Tabla detallada */}
                <div>
                  <Button
                    variant="ghost"
                    className="w-full justify-between"
                    onClick={() => setShowDetails(!showDetails)}
                  >
                    <span>Ver detalle por año</span>
                    {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                  
                  {showDetails && (
                    <div className="mt-4 max-h-[300px] overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Año</TableHead>
                            <TableHead>Total Aportado</TableHead>
                            <TableHead>Saldo Acumulado</TableHead>
                            <TableHead>Rendimiento</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {proyeccion.datosAnuales.map((row) => (
                            <TableRow key={row.anio}>
                              <TableCell className="font-medium">{row.anio}</TableCell>
                              <TableCell>{formatCurrency(row.aportado)}</TableCell>
                              <TableCell className="text-green-600 font-medium">
                                {formatCurrency(row.saldo)}
                              </TableCell>
                              <TableCell className="text-blue-600">
                                {formatCurrency(row.rendimiento)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab Rendimientos Históricos */}
        <TabsContent value="rendimientos" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Rendimientos Históricos por Perfil</CardTitle>
              <CardDescription>Rendimientos anuales de los fondos de inversión (2019-2024)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={Object.keys(RENDIMIENTOS.conservador).map(year => ({
                    year,
                    conservador: (RENDIMIENTOS.conservador[year] * 100).toFixed(2),
                    balanceado: (RENDIMIENTOS.balanceado[year] * 100).toFixed(2),
                    dinamico: (RENDIMIENTOS.dinamico[year] * 100).toFixed(2)
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="year" />
                    <YAxis tickFormatter={(value) => `${value}%`} />
                    <Tooltip formatter={(value) => `${value}%`} />
                    <Legend />
                    <Line type="monotone" dataKey="conservador" stroke="#2d8659" strokeWidth={2} name="Conservador" />
                    <Line type="monotone" dataKey="balanceado" stroke="#1a365d" strokeWidth={2} name="Balanceado" />
                    <Line type="monotone" dataKey="dinamico" stroke="#f59e0b" strokeWidth={2} name="Dinámico" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Perfil</TableHead>
                      {Object.keys(RENDIMIENTOS.conservador).map(year => (
                        <TableHead key={year}>{year}</TableHead>
                      ))}
                      <TableHead>Promedio</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {['conservador', 'balanceado', 'dinamico'].map(perfil => (
                      <TableRow key={perfil}>
                        <TableCell className="font-medium capitalize">{perfil}</TableCell>
                        {Object.values(RENDIMIENTOS[perfil]).map((rend, idx) => (
                          <TableCell key={idx} className={rend >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {(rend * 100).toFixed(2)}%
                          </TableCell>
                        ))}
                        <TableCell className="font-bold">
                          {(PROMEDIOS[perfil] * 100).toFixed(1)}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Comparador */}
        <TabsContent value="comparador" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {['conservador', 'balanceado', 'dinamico'].map(perfil => {
              const tasaMensual = (PROMEDIOS[perfil]) / 12;
              const meses = formData.plazoAnios * 12;
              let saldo = 0;
              
              for (let m = 1; m <= meses; m++) {
                saldo = (saldo + formData.aportacionMensual) * (1 + tasaMensual);
              }
              
              const totalAportado = formData.aportacionMensual * meses;
              
              return (
                <Card key={perfil} className={perfil === formData.perfilInversion ? 'border-[#1a365d] border-2' : ''}>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span className="capitalize">{perfil}</span>
                      {perfil === formData.perfilInversion && (
                        <Badge className="bg-[#1a365d]">Seleccionado</Badge>
                      )}
                    </CardTitle>
                    <CardDescription>
                      Rendimiento promedio: {(PROMEDIOS[perfil] * 100).toFixed(1)}% anual
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="text-center p-4 rounded-lg bg-muted">
                      <p className="text-sm text-muted-foreground">Saldo Final Estimado</p>
                      <p className="text-3xl font-bold text-green-600">
                        {formatCurrency(saldo)}
                      </p>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total aportado:</span>
                        <span className="font-medium">{formatCurrency(totalAportado)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Rendimiento:</span>
                        <span className="font-medium text-blue-600">{formatCurrency(saldo - totalAportado)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Ganancia:</span>
                        <span className="font-medium text-purple-600">
                          {(((saldo - totalAportado) / totalAportado) * 100).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                    <Button
                      variant={perfil === formData.perfilInversion ? 'default' : 'outline'}
                      className="w-full"
                      onClick={() => handleInputChange('perfilInversion', perfil)}
                    >
                      {perfil === formData.perfilInversion ? 'Perfil Actual' : 'Seleccionar'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Disclaimer */}
      <Card className="bg-muted/50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">Aviso Importante</p>
              <p>
                Esta herramienta es únicamente ilustrativa y no constituye una oferta de inversión. 
                Los rendimientos pasados no garantizan rendimientos futuros. 
                Las proyecciones se basan en supuestos que pueden variar según las condiciones del mercado.
                Consulta las condiciones contractuales antes de contratar.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
